/**
 * Authentication Service - Dual Mode (Keycloak + Mock)
 *
 * При useKeycloakAuth=true — делегирует авторизацию Keycloak (redirect flow).
 * При useKeycloakAuth=false — mock auth через localStorage (для локальной разработки).
 *
 * Система permissions (ROLE_PERMISSIONS) работает одинаково в обоих режимах:
 * UserRole определяется из KC realm roles или из mock данных.
 *
 * @story 16.1
 */
import { Injectable, signal, computed, inject, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { KeycloakService, KeycloakEventType } from 'keycloak-angular';
import { startWith, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthActions } from '../../store/actions/auth.actions';
import { CabinetStateService } from './cabinet-state.service';
import { OrgStateService } from './org-state.service';

// ─── User Model (unified) ──────────────────────────────────────────────────────

export enum UserRole {
  ADMIN = 'admin',
  MANAGER = 'manager',
  OPERATOR = 'operator',
  VIEWER = 'viewer',
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  cabinetId: string;
  cabinetName?: string;
  avatar?: string;
}

// ─── Permissions Matrix ─────────────────────────────────────────────────────────

export const ROLE_PERMISSIONS: Record<UserRole, string[]> = {
  [UserRole.ADMIN]: [
    'dashboard.view',
    'products.view', 'products.edit', 'products.delete',
    'orders.view', 'orders.edit',
    'costs.view', 'costs.edit', 'costs.delete',
    'rules.view', 'rules.edit', 'rules.delete',
    'settings.view', 'settings.edit',
    'users.view', 'users.edit', 'users.delete',
    'reports.view', 'reports.export',
    'analytics.view',
  ],
  [UserRole.MANAGER]: [
    'dashboard.view',
    'products.view', 'products.edit',
    'orders.view', 'orders.edit',
    'costs.view', 'costs.edit',
    'rules.view', 'rules.edit',
    'settings.view',
    'reports.view', 'reports.export',
    'analytics.view',
  ],
  [UserRole.OPERATOR]: [
    'dashboard.view',
    'products.view',
    'orders.view', 'orders.edit',
    'costs.view',
    'rules.view',
    'reports.view',
  ],
  [UserRole.VIEWER]: [
    'dashboard.view',
    'products.view',
    'orders.view',
    'costs.view',
    'reports.view',
  ],
};

// ─── Keycloak Role Mapping ──────────────────────────────────────────────────────

const KC_ROLE_MAP: Record<string, UserRole> = {
  'admin': UserRole.ADMIN,
  'cabinet_owner': UserRole.MANAGER,
  'cabinet_manager': UserRole.OPERATOR,
  'cabinet_viewer': UserRole.VIEWER,
};

const KC_ROLE_PRIORITY: string[] = [
  'admin', 'cabinet_owner', 'cabinet_manager', 'cabinet_viewer',
];

function mapKeycloakRole(kcRoles: string[]): UserRole {
  for (const kcRole of KC_ROLE_PRIORITY) {
    if (kcRoles.includes(kcRole) && KC_ROLE_MAP[kcRole]) {
      return KC_ROLE_MAP[kcRole];
    }
  }
  return UserRole.VIEWER;
}

// ─── Mock Auth Types ────────────────────────────────────────────────────────────

export interface AuthResponse {
  success: boolean;
  message: string;
  user?: User;
  token?: string;
}

export interface RegisterData {
  email: string;
  password: string;
  name: string;
  companyName?: string;
}

export interface LoginData {
  email: string;
  password: string;
  rememberMe?: boolean;
}

// Storage keys (mock mode only)
const STORAGE_KEYS = {
  USERS: 'levea_users',
  CURRENT_USER: 'levea_current_user',
  TOKEN: 'levea_token',
  REMEMBER_ME: 'levea_remember_me',
};

// ─── Auth Service ───────────────────────────────────────────────────────────────

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly useKeycloak = environment.useKeycloakAuth;
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly cabinetState = inject(CabinetStateService);
  private readonly orgState = inject(OrgStateService);

  // KeycloakService — @Optional() чтобы не падать при mock mode (не зарегистрирован)
  private readonly keycloak: KeycloakService | null;

  // Reactive state
  private _currentUser = signal<User | null>(null);
  private _isAuthenticated = signal(false);
  private _isLoading = signal(false);
  // Fix auth-401-zombie r3 (P0): защёлка "сессия была РЕАЛЬНО хоть раз
  // аутентифицирована" — см. hasEverAuthenticated() ниже.
  private _wasAuthenticated = false;

  // Public computed signals
  readonly currentUser = computed(() => this._currentUser());
  // CRITICAL: Use reactive signal updated by Keycloak events subscription
  readonly isAuthenticated = computed(() => this._isAuthenticated());
  readonly isLoading = computed(() => this._isLoading());
  readonly userRole = computed(() => this._currentUser()?.role || null);
  readonly userName = computed(() => this._currentUser()?.name || '');
  readonly userEmail = computed(() => this._currentUser()?.email || '');

  constructor(@Optional() keycloakService: KeycloakService | null) {
    console.log('[AuthService Constructor] Starting...');
    this.keycloak = keycloakService;
    console.log('[AuthService Constructor] Keycloak service:', this.keycloak ? 'PRESENT' : 'NULL');
    console.log('[AuthService Constructor] useKeycloak:', this.useKeycloak);

    if (this.useKeycloak && this.keycloak) {
      console.log('[AuthService Constructor] Entering Keycloak mode...');

      // Fix auth-401-zombie r4 (P0, gpt): защёлкивает _wasAuthenticated
      // ЭАГЕРЛИ (в момент фактического успеха), а не лениво при первом
      // вызове hasEverAuthenticated(). Без этого: если clearToken()
      // (упавший refresh, напр. из WebSocket-reconnect пути) случается ДО
      // первого вызова hasEverAuthenticated(), факт авторизации теряется
      // безвозвратно — защищённый запрос снова уходит анонимно. Покрывает
      // login/refresh ПОСЛЕ bootstrap; для самого bootstrap-момента —
      // см. markAuthenticated() из keycloak-init.factory.ts.
      this.keycloak.keycloakEvents$.subscribe((event) => {
        if (
          event.type === KeycloakEventType.OnAuthSuccess ||
          event.type === KeycloakEventType.OnAuthRefreshSuccess
        ) {
          this.markAuthenticated();
        }
      });

      // Используем startWith() для немедленной проверки + будущих Keycloak событий.
      // startWith(undefined) срабатывает синхронно → switchMap немедленно вызывает isLoggedIn().
      // Это заменяет отдельный initializeKeycloakAuth() и исключает двойной вызов loadKeycloakUser().
      this.keycloak.keycloakEvents$.pipe(
        startWith(undefined), // Trigger immediate check
        switchMap(async () => {
          // Re-check auth state на каждое событие (включая initial)
          const isLoggedIn = await this.keycloak!.isLoggedIn();
          return isLoggedIn;
        })
      ).subscribe(async (isLoggedIn) => {
        console.log('[AuthService] Auth state check:', isLoggedIn);
        // НЕ устанавливаем _isAuthenticated=true здесь!
        // loadKeycloakUser() установит его ПОСЛЕ загрузки кабинетов и пользователя.
        // Это предотвращает race condition: AppComponent видит isAuthenticated=true
        // пока _currentUser=null и _cabinets=[] → роль viewer.
        if (isLoggedIn) {
          await this.loadKeycloakUser();
        } else {
          this._isAuthenticated.set(false);
          this._currentUser.set(null);
        }
      });
    } else {
      this.initializeMockAuth();
      this.seedDemoUsers();
    }
  }

  // ─── Keycloak Auth ──────────────────────────────────────────────────────────

  private async loadKeycloakUser(): Promise<void> {
    if (!this.keycloak) return;

    try {
      const profile = await this.keycloak.loadUserProfile();
      const roles = this.keycloak.getUserRoles(true);
      const tokenParsed = this.keycloak.getKeycloakInstance().tokenParsed;

      const cabinetIds: string[] = tokenParsed?.['cabinet_ids'] || [];

      const user: User = {
        id: tokenParsed?.sub || profile.id || '',
        email: profile.email || '',
        name: [profile.firstName, profile.lastName].filter(Boolean).join(' ')
          || profile.username || '',
        role: mapKeycloakRole(roles),
        cabinetId: cabinetIds[0] || '',
        avatar: undefined,
      };

      // Story 21.9: загрузить кабинеты ДО установки currentUser.
      // Критично: _currentRole() вычисляется при первом рендере сразу после
      // _currentUser.set() — если кабинеты ещё не загружены, activeCabinet()
      // вернёт null и роль упадёт в 'viewer' (race condition).
      try {
        await this.cabinetState.loadCabinets();
      } catch (err) {
        console.warn('[AuthService] Failed to load cabinets:', err);
      }

      // Story 110.7: загрузить организации пользователя (org-scope).
      // ПОСЛЕ кабинетов; org-scope не должен ронять логин — отдельный try/catch.
      try {
        await this.orgState.loadMyOrgs();
      } catch (err) {
        console.warn('[AuthService] Failed to load orgs:', err);
      }

      this._currentUser.set(user);
      this._isAuthenticated.set(true);

      // Синхронизируем NGRX store с KC state
      this.store.dispatch(AuthActions.keycloakInitSuccess({
        user,
        token: this.keycloak?.getKeycloakInstance().token || undefined,
      }));
    } catch (err) {
      console.error('Failed to load Keycloak user profile:', err);
      this._currentUser.set(null);
      this._isAuthenticated.set(false);
    }
  }

  // ─── Public Auth Methods ────────────────────────────────────────────────────

  async login(data?: LoginData): Promise<AuthResponse | void> {
    if (this.useKeycloak && this.keycloak) {
      await this.keycloak.login({
        redirectUri: window.location.origin + '/dashboard',
      });
      return;
    }
    if (data) {
      return this.mockLogin(data);
    }
  }

  async register(data?: RegisterData): Promise<AuthResponse | void> {
    if (this.useKeycloak && this.keycloak) {
      await this.keycloak.register({
        redirectUri: window.location.origin + '/dashboard',
      });
      return;
    }
    if (data) {
      return this.mockRegister(data);
    }
  }

  async logout(): Promise<void> {
    // Story 21.9: очистить состояние кабинета при логауте
    this.cabinetState.clear();
    // Story 110.7: очистить состояние организации при логауте
    this.orgState.clear();
    if (this.useKeycloak && this.keycloak) {
      this._currentUser.set(null);
      this._isAuthenticated.set(false);
      await this.keycloak.logout(window.location.origin + '/welcome');
      return;
    }
    this.clearMockAuth();
    this.router.navigate(['/auth/login']);
  }

  getToken(): string | null {
    if (this.useKeycloak && this.keycloak) {
      return this.keycloak.getKeycloakInstance().token || null;
    }
    return localStorage.getItem(STORAGE_KEYS.TOKEN);
  }

  /**
   * Получить свежий токен с автоматическим refresh (для WS reconnect).
   * Keycloak: вызывает updateToken(minValidity) — обновляет если до истечения
   * < minValidity сек. Mock: возвращает токен из localStorage.
   *
   * Fix auth-401-zombie r2 (P1, gpt): при force=true передаётся minValidity=-1
   * — keycloak-js трактует это как БЕЗУСЛОВНЫЙ refresh (см. исходник
   * keycloak.js: `if (minValidity == -1) { refreshToken = true; }`),
   * независимо от того, что клиентские часы считают токен ещё валидным.
   * Нужно для error.interceptor: сервер вернул 401 на токен, который
   * updateToken(30) счёл бы "ещё достаточно свежим" (сервер мог отклонить
   * его по ДРУГОЙ причине — отзыв, рассинхрон часов) — обычный refresh
   * вернул бы ТОТ ЖЕ токен, retry гарантированно получил бы второй 401.
   *
   * Fix auth-401-zombie r3 (P1, gpt): keycloak-angular's KeycloakService.
   * updateToken() ГЛОТАЕТ исключение из нижележащего keycloak-js и
   * возвращает `false` вместо повторного throw (см. исходник
   * keycloak.service: `try { return await this._instance.updateToken(...) }
   * catch (error) { return false; }`). Наш try/catch НИКОГДА не срабатывал
   * на неудачном refresh — мы просто проваливались дальше и возвращали
   * СТАРЫЙ (стухший) токен как будто "свежий". `refreshed === false`
   * означает ЛИБО "токен и так был валиден, обновление не требовалось"
   * (нормально, можно отдать текущий), ЛИБО "обновление понадобилось и
   * упало" (токен сейчас истёк — обязаны вернуть null, не токен). В
   * force-режиме `false` — ВСЕГДА неудача (force гарантирует, что попытка
   * обновления реально была сделана).
   */
  async getFreshToken(force = false): Promise<string | null> {
    if (this.useKeycloak && this.keycloak) {
      try {
        const refreshed = await this.keycloak.updateToken(force ? -1 : 30);
        if (!refreshed) {
          if (force || this.keycloak.isTokenExpired(0)) {
            return null;
          }
        }
        return this.keycloak.getKeycloakInstance().token || null;
      } catch (err) {
        console.warn('[Auth] getFreshToken: Keycloak refresh failed', err instanceof Error ? err.message : err);
        return null;
      }
    }
    return localStorage.getItem(STORAGE_KEYS.TOKEN);
  }

  /**
   * Fix auth-401-zombie r2 (P0, обе lane): true если Keycloak КОГДА-ЛИБО
   * успешно аутентифицировал пользователя в этой сессии (даже если
   * access-токен с тех пор истёк) — в отличие от "сессии никогда не было"
   * (анонимный визит на public-роут/эндпоинт без auth-guard). Раньше
   * authInterceptor форсировал login()-редирект ДАЖЕ для анонимов —
   * ломало bootstrap (ngx-translate грузит /assets/i18n/*.json) и публичные
   * страницы (Welcome, /calculator). login() должен форситься ТОЛЬКО когда
   * сессия РЕАЛЬНО была и протухла, не когда её не было вовсе.
   *
   * Fix auth-401-zombie r3 (P0, gpt — критично, возврат зомби-сессии другим
   * путём): раньше эта функция читала ТЕКУЩЕЕ keycloak.authenticated живьём
   * при каждом вызове. Keycloak при 400 на refresh делает clearToken() ->
   * authenticated=false — протухшая (но РЕАЛЬНО существовавшая) сессия
   * начинала классифицироваться как "аноним", защищённый запрос уходил БЕЗ
   * Authorization без единой попытки refresh/login. Теперь: флаг
   * _wasAuthenticated защёлкивается (латчится) один раз при первом
   * реальном observation authenticated===true и остаётся true НАВСЕГДА в
   * рамках этой JS-сессии (страница перезагрузится при реальном логауте/
   * редиректе — состояние обнулится естественно).
   *
   * Fix auth-401-zombie r4 (P0, gpt — остаточный race): защёлка r3 была
   * ЛЕНИВОЙ — ставилась ТОЛЬКО внутри этого метода. Если clearToken()
   * (упавший refresh, например из WebSocket-reconnect пути) случался ДО
   * ПЕРВОГО вызова hasEverAuthenticated() — факт авторизации терялся
   * безвозвратно, метод впервые наблюдал authenticated=false и НАВСЕГДА
   * (ошибочно) решал "сессии не было". Теперь защёлка СТАВИТСЯ ЭАГЕРЛИ —
   * см. markAuthenticated(), вызываемый (а) из keycloak-init.factory.ts
   * сразу после успешного init(), и (б) из подписки на
   * OnAuthSuccess/OnAuthRefreshSuccess в конструкторе выше (покрывает login
   * ПОСЛЕ bootstrap). Проверка здесь остаётся ТРЕТЬИМ рубежом на случай,
   * если оба эагерных пути почему-то не сработали.
   */
  markAuthenticated(): void {
    this._wasAuthenticated = true;
  }

  hasEverAuthenticated(): boolean {
    if (this.useKeycloak && this.keycloak) {
      if (this._wasAuthenticated) return true;
      try {
        if (this.keycloak.getKeycloakInstance().authenticated === true) {
          this.markAuthenticated();
          return true;
        }
      } catch {
        // ignore — falls through to false below
      }
      return false;
    }
    return false;
  }

  /**
   * Fix auth-401-zombie: истекает ли текущий Keycloak-токен в ближайшие
   * minValiditySeconds секунд (или уже истёк). Используется интерсепторами
   * для проактивного silent refresh ДО отправки запроса — вместо того,
   * чтобы слать запрос со стухшим токеном и ловить анонимный 401 постфактум.
   * Mock-режим: всегда false (там нет Keycloak-семантики истечения).
   */
  isTokenExpiringSoon(minValiditySeconds = 30): boolean {
    if (this.useKeycloak && this.keycloak) {
      try {
        return this.keycloak.isTokenExpired(minValiditySeconds);
      } catch (err) {
        console.warn('[Auth] isTokenExpiringSoon: check failed, assuming expired', err instanceof Error ? err.message : err);
        return true;
      }
    }
    return false;
  }

  // ─── Permission & Role Methods (работают одинаково в обоих режимах) ────────

  hasPermission(permission: string): boolean {
    const role = this._currentUser()?.role;
    if (!role) return false;
    return (ROLE_PERMISSIONS[role] || []).includes(permission);
  }

  hasAnyPermission(permissions: string[]): boolean {
    return permissions.some(p => this.hasPermission(p));
  }

  hasAllPermissions(permissions: string[]): boolean {
    return permissions.every(p => this.hasPermission(p));
  }

  hasRole(role: UserRole | UserRole[]): boolean {
    const userRole = this._currentUser()?.role;
    if (!userRole) return false;
    if (Array.isArray(role)) {
      return role.includes(userRole);
    }
    return userRole === role;
  }

  isAdmin(): boolean {
    return this.hasRole(UserRole.ADMIN);
  }

  getPermissions(): string[] {
    const role = this._currentUser()?.role;
    if (!role) return [];
    return ROLE_PERMISSIONS[role] || [];
  }

  // ─── Mock Auth (localStorage) ─────────────────────────────────────────────

  private initializeMockAuth(): void {
    const storedUser = localStorage.getItem(STORAGE_KEYS.CURRENT_USER);
    const storedToken = localStorage.getItem(STORAGE_KEYS.TOKEN);

    if (storedUser && storedToken) {
      try {
        const user = JSON.parse(storedUser) as User;
        this._currentUser.set(user);
        this._isAuthenticated.set(true);
      } catch {
        this.clearMockAuth();
      }
    }
  }

  private seedDemoUsers(): void {
    // Never seed mock credentials in production builds
    if (environment.production) return;
    const existingUsers = this.getStoredUsers();
    if (existingUsers.length > 0) return;

    const demoUsers: Array<{ email: string; password: string; name: string; role: UserRole }> = [
      { email: 'admin@ealev.ru', password: 'admin123', name: 'Администратор', role: UserRole.ADMIN },
      { email: 'manager@ealev.ru', password: 'manager123', name: 'Менеджер Иванов', role: UserRole.MANAGER },
      { email: 'operator@ealev.ru', password: 'operator123', name: 'Оператор Петров', role: UserRole.OPERATOR },
      { email: 'viewer@ealev.ru', password: 'viewer123', name: 'Наблюдатель Сидоров', role: UserRole.VIEWER },
      { email: 'demo@ealev.ru', password: 'demo123', name: 'Demo User', role: UserRole.MANAGER },
    ];

    const users: Array<User & { password: string }> = demoUsers.map((u, i) => ({
      id: `user-${i + 1}`,
      email: u.email,
      password: u.password,
      name: u.name,
      role: u.role,
      cabinetId: '1b6844f1-5140-4bd8-b90d-6be7ad6586e8',
      cabinetName: 'Levea',
    }));

    localStorage.setItem(STORAGE_KEYS.USERS, JSON.stringify(users));
  }

  private getStoredUsers(): Array<User & { password: string }> {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.USERS);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  private generateToken(user: User): string {
    const payload = {
      sub: user.id,
      email: user.email,
      role: user.role,
      cabinet_id: user.cabinetId,
      exp: Date.now() + 24 * 60 * 60 * 1000,
    };
    return btoa(JSON.stringify(payload));
  }

  private async mockLogin(data: LoginData): Promise<AuthResponse> {
    this._isLoading.set(true);
    await this.delay(800);

    try {
      // Гарантируем наличие demo users
      this.ensureDemoUsersExist();

      const users = this.getStoredUsers();
      const user = users.find(
        u => u.email.toLowerCase() === data.email.toLowerCase() && u.password === data.password
      );

      if (!user) {
        return { success: false, message: 'Неверный email или пароль' };
      }

      const { password: _, ...userWithoutPassword } = user;
      const token = this.generateToken(userWithoutPassword);

      this._currentUser.set(userWithoutPassword);
      this._isAuthenticated.set(true);
      localStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(userWithoutPassword));
      localStorage.setItem(STORAGE_KEYS.TOKEN, token);

      if (data.rememberMe) {
        localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, 'true');
      }

      return { success: true, message: 'Вход выполнен успешно', user: userWithoutPassword, token };
    } finally {
      this._isLoading.set(false);
    }
  }

  private async mockRegister(data: RegisterData): Promise<AuthResponse> {
    this._isLoading.set(true);
    await this.delay(800);

    try {
      const users = this.getStoredUsers();

      if (users.some(u => u.email.toLowerCase() === data.email.toLowerCase())) {
        return { success: false, message: 'Пользователь с таким email уже зарегистрирован' };
      }

      if (data.password.length < 6) {
        return { success: false, message: 'Пароль должен содержать минимум 6 символов' };
      }

      const newUser: User & { password: string } = {
        id: `user-${Date.now()}`,
        email: data.email,
        password: data.password,
        name: data.name,
        role: UserRole.VIEWER,
        cabinetId: `cabinet-${Date.now()}`,
        cabinetName: data.companyName || `${data.name}'s Cabinet`,
      };

      users.push(newUser);
      localStorage.setItem(STORAGE_KEYS.USERS, JSON.stringify(users));

      const { password: _, ...userWithoutPassword } = newUser;
      const token = this.generateToken(userWithoutPassword);

      this._currentUser.set(userWithoutPassword);
      this._isAuthenticated.set(true);
      localStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(userWithoutPassword));
      localStorage.setItem(STORAGE_KEYS.TOKEN, token);

      return { success: true, message: 'Регистрация успешна', user: userWithoutPassword, token };
    } finally {
      this._isLoading.set(false);
    }
  }

  private ensureDemoUsersExist(): void {
    // Never seed mock credentials in production builds
    if (environment.production) return;
    const existingUsers = this.getStoredUsers();
    const demoEmails = ['admin@ealev.ru', 'manager@ealev.ru', 'operator@ealev.ru', 'viewer@ealev.ru', 'demo@ealev.ru'];
    const existingEmails = existingUsers.map(u => u.email.toLowerCase());
    const missing = demoEmails.filter(e => !existingEmails.includes(e.toLowerCase()));

    if (missing.length === 0) return;

    const demoUsers: Array<{ email: string; password: string; name: string; role: UserRole }> = [
      { email: 'admin@ealev.ru', password: 'admin123', name: 'Администратор', role: UserRole.ADMIN },
      { email: 'manager@ealev.ru', password: 'manager123', name: 'Менеджер Иванов', role: UserRole.MANAGER },
      { email: 'operator@ealev.ru', password: 'operator123', name: 'Оператор Петров', role: UserRole.OPERATOR },
      { email: 'viewer@ealev.ru', password: 'viewer123', name: 'Наблюдатель Сидоров', role: UserRole.VIEWER },
      { email: 'demo@ealev.ru', password: 'demo123', name: 'Demo User', role: UserRole.MANAGER },
    ];

    const newUsers: Array<User & { password: string }> = demoUsers
      .filter(u => missing.includes(u.email))
      .map((u, i) => ({
        id: `user-demo-${Date.now()}-${i}`,
        email: u.email,
        password: u.password,
        name: u.name,
        role: u.role,
        cabinetId: '1b6844f1-5140-4bd8-b90d-6be7ad6586e8',
        cabinetName: 'Levea',
      }));

    localStorage.setItem(STORAGE_KEYS.USERS, JSON.stringify([...existingUsers, ...newUsers]));
  }

  private clearMockAuth(): void {
    this._currentUser.set(null);
    this._isAuthenticated.set(false);
    localStorage.removeItem(STORAGE_KEYS.CURRENT_USER);
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
    localStorage.removeItem('isAuthenticated');
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
